// 4-px stacked bar with a coloured-dot legend below. Renders inside a metric
// card to break a single aggregate value into its component segments
// (contract / SOWs / variations on the Total contract value card; reusable
// for any "parts of the whole" surface). The bar itself is decorative — the
// legend below is the screen-reader path; consumers should also pass an
// `aria-label` to the parent metric card summarising the breakdown in plain
// language. See brand SKILL.md §9.5.
//
// An optional limit marker draws a rule across the track at a given
// proportion, for a whole that has a threshold inside it — a contract value
// the billing has passed, say. It lives here rather than in the caller
// because only this component knows where the track sits and how tall it is.

import { clsx } from "clsx";

export interface StackedBarSegment {
  /** Stable key for React reconciliation. */
  key: string;
  /** Magnitude in the same unit as `total`. Negative segments are sized by
   *  absolute magnitude — see brand SKILL.md §9.5 negative-segment rules. */
  value: number;
  /** Legend text rendered next to the colour swatch. */
  label: string;
  /** Tailwind background class for both the bar segment and the legend
   *  swatch (e.g. `bg-brand-500 dark:bg-brand-400`). */
  colorClass: string;
  /** Optional formatted currency / units string rendered next to the
   *  label in the legend. The numeric `value` is always available for
   *  proportional sizing; the formatted string controls display. */
  formattedValue?: string;
}

/** A rule drawn across the track at `atPct`, with its own legend entry. */
export interface StackedBarLimitMarker {
  /** Position along the track, 0–100. */
  atPct: number;
  /** Legend text naming what the rule marks. */
  label: string;
  /** Tailwind background class for the rule and its legend swatch. */
  colorClass: string;
}

export interface StackedBarBreakdownProps {
  segments: StackedBarSegment[];
  /** Total used to compute segment widths. When `total <= 0`, the bar
   *  itself is suppressed (a zero-or-negative total renders as a flat
   *  empty track) but the legend still renders so the breakdown stays
   *  inspectable. */
  total: number;
  /** Test hook — applied as `data-testid` on the outer wrapper. */
  testId?: string;
  /** Marks a threshold inside the whole. Omitted when there is nothing to
   *  mark, so an ordinary breakdown draws exactly as before. */
  limitMarker?: StackedBarLimitMarker;
}

/** Suppress the bar when there is only a single visible segment — a
 *  100%-of-one bar adds visual noise without communicating anything,
 *  per brand SKILL.md §9.5. */
function shouldHideBar(segments: StackedBarSegment[]): boolean {
  return segments.filter((s) => s.value !== 0).length <= 1;
}

export default function StackedBarBreakdown({
  segments,
  total,
  testId,
  limitMarker,
}: StackedBarBreakdownProps) {
  const hideBar = shouldHideBar(segments) || total <= 0;

  return (
    <div data-testid={testId}>
      {!hideBar && (
        <div aria-hidden="true" className="relative mt-2">
          <div className="flex h-1 gap-px overflow-hidden rounded-sm bg-slate-100 dark:bg-slate-700">
            {segments.map((seg) => {
              const width = total > 0 ? (Math.abs(seg.value) / total) * 100 : 0;
              if (width <= 0) return null;
              return (
                <div
                  key={seg.key}
                  style={{ width: `${width}%` }}
                  className={seg.colorClass}
                />
              );
            })}
          </div>
          {/* A sibling of the track rather than a child of it. Inside the
              clipped track the rule is judged against the segment colour
              beneath it — a red rule on emerald or amber measures about
              1.9:1 — and its ends are cut off at the track edge. Standing it
              proud of the track puts those ends on the card surface, where
              the same colour measures comfortably past the AA target. */}
          {limitMarker && (
            <div
              data-testid={testId ? `${testId}-limit` : undefined}
              style={{ left: `${limitMarker.atPct}%` }}
              className={clsx(
                "absolute -inset-y-1 w-0.5 rounded-sm",
                limitMarker.colorClass,
              )}
            />
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2.5 mt-2">
        {segments.map((seg) => (
          <span
            key={seg.key}
            data-testid={testId ? `${testId}-legend-${seg.key}` : undefined}
            className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 dark:text-slate-400"
          >
            <span
              aria-hidden="true"
              className={clsx("w-2 h-2 rounded-sm", seg.colorClass)}
            />
            {seg.label}
            {seg.formattedValue !== undefined && ` ${seg.formattedValue}`}
          </span>
        ))}
        {limitMarker && (
          <span
            data-testid={testId ? `${testId}-legend-limit` : undefined}
            className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 dark:text-slate-400"
          >
            <span
              aria-hidden="true"
              className={clsx("w-0.5 h-2.5 rounded-sm", limitMarker.colorClass)}
            />
            {limitMarker.label}
          </span>
        )}
      </div>
    </div>
  );
}
